/**
 * Enhanced History Tab Component - Phase 1 Improvements
 * 
 * Features:
 * - Advanced filtering with date range picker
 * - Expandable row details
 * - Export to CSV/PDF
 * - Full keyboard navigation
 * - 508 compliant
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Collapse,
  TextField,
  MenuItem,
  Button,
  Grid,
  Tooltip,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  FilterList as FilterIcon,
  GetApp as DownloadIcon,
  PictureAsPdf as PdfIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Security as SecurityIcon,
  Description as DescriptionIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
// Using native HTML5 date inputs for better compatibility

// USCIS Colors
const USCIS_BLUE = '#003366';

// Action type configuration
const ACTION_TYPE_CONFIG: Record<string, { icon: React.ReactElement; color: string; label: string }> = {
  created: { icon: <AddIcon />, color: '#4caf50', label: 'Created' },
  updated: { icon: <EditIcon />, color: '#2196f3', label: 'Updated' },
  deleted: { icon: <DeleteIcon />, color: '#d32f2f', label: 'Deleted' },
  metadata: { icon: <DescriptionIcon />, color: '#1976d2', label: 'Metadata' },
  governance: { icon: <SecurityIcon />, color: '#f57c00', label: 'Governance' },
  schema: { icon: <DescriptionIcon />, color: '#7b1fa2', label: 'Schema' },
  quality: { icon: <SecurityIcon />, color: '#388e3c', label: 'Quality' },
};

export interface HistoryEntry {
  id: string;
  action: string;
  actionType: keyof typeof ACTION_TYPE_CONFIG;
  user: string;
  userEmail?: string;
  date: Date;
  description: string;
  details?: {
    field?: string;
    oldValue?: string;
    newValue?: string;
    reason?: string;
    metadata?: Record<string, any>;
  };
  relatedEntity?: string;
  category?: string;
}

interface EnhancedHistoryTabProps {
  entries: HistoryEntry[];
  title?: string;
  showFilters?: boolean;
  showExport?: boolean;
  maxHeight?: string;
}

const EnhancedHistoryTab: React.FC<EnhancedHistoryTabProps> = ({
  entries,
  title = 'History',
  showFilters = true,
  showExport = true,
  maxHeight = '600px',
}) => {
  // State
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [filteredEntries, setFilteredEntries] = useState<HistoryEntry[]>(entries);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionTypeFilter, setActionTypeFilter] = useState<string>('all');
  const [userFilter, setUserFilter] = useState<string>('all');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [focusedRowIndex, setFocusedRowIndex] = useState<number>(-1);
  const tableRef = useRef<HTMLDivElement>(null);

  // Get unique users and action types for filters
  const uniqueUsers = Array.from(new Set(entries.map(e => e.user)));
  const uniqueActionTypes = Array.from(new Set(entries.map(e => e.actionType)));

  // Apply filters
  useEffect(() => {
    let filtered = [...entries];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        entry =>
          entry.action.toLowerCase().includes(term) ||
          entry.description.toLowerCase().includes(term) ||
          entry.user.toLowerCase().includes(term)
      );
    }

    // Action type filter
    if (actionTypeFilter !== 'all') {
      filtered = filtered.filter(entry => entry.actionType === actionTypeFilter);
    }

    // User filter
    if (userFilter !== 'all') {
      filtered = filtered.filter(entry => entry.user === userFilter);
    }

    // Date range filter
    if (startDate) {
      filtered = filtered.filter(entry => entry.date >= startDate);
    }
    if (endDate) {
      const endOfDay = new Date(endDate);
      endOfDay.setHours(23, 59, 59, 999);
      filtered = filtered.filter(entry => entry.date <= endOfDay);
    }

    setFilteredEntries(filtered);
  }, [entries, searchTerm, actionTypeFilter, userFilter, startDate, endDate]);

  // Toggle row expansion
  const toggleRow = (id: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setActionTypeFilter('all');
    setUserFilter('all');
    setStartDate(null);
    setEndDate(null);
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Date', 'Action', 'Type', 'User', 'Description'];
    const csvData = filteredEntries.map(entry => [
      entry.date.toLocaleString(),
      entry.action,
      ACTION_TYPE_CONFIG[entry.actionType]?.label || entry.actionType,
      entry.user,
      entry.description,
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `history_export_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Export to PDF (opens print dialog)
  const exportToPDF = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>History Report - ${new Date().toLocaleDateString()}</title>
          <style>
            body { font-family: 'Source Sans Pro', Arial, sans-serif; margin: 20px; }
            h1 { color: ${USCIS_BLUE}; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: ${USCIS_BLUE}; color: white; }
            .badge { display: inline-block; padding: 2px 8px; border-radius: 12px; font-size: 12px; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          <h1>${title} Report</h1>
          <p>Generated: ${new Date().toLocaleString()}</p>
          <p>Total Entries: ${filteredEntries.length}</p>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Action</th>
                <th>Type</th>
                <th>User</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              ${filteredEntries
                .map(
                  entry => `
                <tr>
                  <td>${entry.date.toLocaleString()}</td>
                  <td>${entry.action}</td>
                  <td><span class="badge" style="background-color: ${ACTION_TYPE_CONFIG[entry.actionType]?.color}">${ACTION_TYPE_CONFIG[entry.actionType]?.label || entry.actionType}</span></td>
                  <td>${entry.user}</td>
                  <td>${entry.description}</td>
                </tr>
              `
                )
                .join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!tableRef.current) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusedRowIndex(prev => Math.min(prev + 1, filteredEntries.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedRowIndex(prev => Math.max(prev - 1, 0));
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (focusedRowIndex >= 0 && focusedRowIndex < filteredEntries.length) {
            toggleRow(filteredEntries[focusedRowIndex].id);
          }
          break;
        case 'Escape':
          setFocusedRowIndex(-1);
          break;
      }
    };

    const tableElement = tableRef.current;
    if (tableElement) {
      tableElement.addEventListener('keydown', handleKeyDown);
      return () => tableElement.removeEventListener('keydown', handleKeyDown);
    }
  }, [focusedRowIndex, filteredEntries, toggleRow]);

  return (
    <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, color: USCIS_BLUE }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {filteredEntries.length} of {entries.length} entries
            </Typography>
          </Box>
          
          {showExport && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Export to CSV">
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={exportToCSV}
                  size="small"
                  aria-label="Export history to CSV"
                >
                  CSV
                </Button>
              </Tooltip>
              <Tooltip title="Export to PDF">
                <Button
                  variant="outlined"
                  startIcon={<PdfIcon />}
                  onClick={exportToPDF}
                  size="small"
                  aria-label="Export history to PDF"
                >
                  PDF
                </Button>
              </Tooltip>
            </Box>
          )}
        </Box>

        {/* Filters */}
        {showFilters && (
          <Paper sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <FilterIcon sx={{ mr: 1, color: USCIS_BLUE }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Filters
              </Typography>
              {(searchTerm || actionTypeFilter !== 'all' || userFilter !== 'all' || startDate || endDate) && (
                <Button
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={clearFilters}
                  sx={{ ml: 'auto' }}
                  aria-label="Clear all filters"
                >
                  Clear All
                </Button>
              )}
            </Box>

            <Grid container spacing={2}>
              {/* Search */}
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder="Search actions, users..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  aria-label="Search history entries"
                />
              </Grid>

              {/* Action Type Filter */}
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="action-type-filter-label">Action Type</InputLabel>
                  <Select
                    labelId="action-type-filter-label"
                    value={actionTypeFilter}
                    label="Action Type"
                    onChange={(e: SelectChangeEvent) => setActionTypeFilter(e.target.value)}
                    aria-label="Filter by action type"
                  >
                    <MenuItem value="all">All Types</MenuItem>
                    {uniqueActionTypes.map(type => (
                      <MenuItem key={type} value={type}>
                        {ACTION_TYPE_CONFIG[type]?.label || type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* User Filter */}
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="user-filter-label">User</InputLabel>
                  <Select
                    labelId="user-filter-label"
                    value={userFilter}
                    label="User"
                    onChange={(e: SelectChangeEvent) => setUserFilter(e.target.value)}
                    aria-label="Filter by user"
                  >
                    <MenuItem value="all">All Users</MenuItem>
                    {uniqueUsers.map(user => (
                      <MenuItem key={user} value={user}>
                        {user}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Date Range */}
              <Grid item xs={12} md={1.5}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={startDate ? startDate.toISOString().split('T')[0] : ''}
                  onChange={(e) => setStartDate(e.target.value ? new Date(e.target.value) : null)}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={1.5}>
                <TextField
                  label="End Date"
                  type="date"
                  value={endDate ? endDate.toISOString().split('T')[0] : ''}
                  onChange={(e) => setEndDate(e.target.value ? new Date(e.target.value) : null)}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* History Table */}
        <TableContainer
          component={Paper}
          ref={tableRef}
          sx={{ maxHeight }}
          tabIndex={0}
          role="region"
          aria-label="History entries table"
        >
          <Table stickyHeader aria-label="history table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ bgcolor: USCIS_BLUE, color: 'white', width: 50 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Details
                  </Typography>
                </TableCell>
                <TableCell sx={{ bgcolor: USCIS_BLUE, color: 'white' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Date
                  </Typography>
                </TableCell>
                <TableCell sx={{ bgcolor: USCIS_BLUE, color: 'white' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Action
                  </Typography>
                </TableCell>
                <TableCell sx={{ bgcolor: USCIS_BLUE, color: 'white' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Type
                  </Typography>
                </TableCell>
                <TableCell sx={{ bgcolor: USCIS_BLUE, color: 'white' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    User
                  </Typography>
                </TableCell>
                <TableCell sx={{ bgcolor: USCIS_BLUE, color: 'white' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Description
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEntries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No history entries found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredEntries.map((entry, index) => {
                  const isExpanded = expandedRows.has(entry.id);
                  const isFocused = index === focusedRowIndex;
                  const config = ACTION_TYPE_CONFIG[entry.actionType] || ACTION_TYPE_CONFIG.updated;

                  return (
                    <React.Fragment key={entry.id}>
                      <TableRow
                        hover
                        sx={{
                          cursor: 'pointer',
                          bgcolor: isFocused ? '#e3f2fd' : 'inherit',
                          '&:focus': {
                            outline: `2px solid ${USCIS_BLUE}`,
                            outlineOffset: '-2px',
                          },
                        }}
                        onClick={() => toggleRow(entry.id)}
                        tabIndex={0}
                        role="button"
                        aria-expanded={isExpanded}
                        aria-label={`${entry.action} by ${entry.user} on ${entry.date.toLocaleString()}`}
                      >
                        <TableCell>
                          <IconButton
                            size="small"
                            aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                          >
                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {entry.date.toLocaleDateString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {entry.date.toLocaleTimeString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {entry.action}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={React.cloneElement(config.icon, { sx: { fontSize: 16 } })}
                            label={config.label}
                            size="small"
                            sx={{
                              bgcolor: config.color,
                              color: 'white',
                              '& .MuiChip-icon': { color: 'white' },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Box>
                              <Typography variant="body2">{entry.user}</Typography>
                              {entry.userEmail && (
                                <Typography variant="caption" color="text.secondary">
                                  {entry.userEmail}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{entry.description}</Typography>
                        </TableCell>
                      </TableRow>

                      {/* Expandable Details Row */}
                      <TableRow>
                        <TableCell colSpan={6} sx={{ p: 0, borderBottom: isExpanded ? '1px solid #e0e0e0' : 'none' }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 3, bgcolor: '#f9f9f9' }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                                Additional Details
                              </Typography>
                              
                              {entry.details && (
                                <Grid container spacing={2}>
                                  {entry.details.field && (
                                    <Grid item xs={12} md={4}>
                                      <Typography variant="caption" color="text.secondary">
                                        Field Changed
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {entry.details.field}
                                      </Typography>
                                    </Grid>
                                  )}
                                  {entry.details.oldValue && (
                                    <Grid item xs={12} md={4}>
                                      <Typography variant="caption" color="text.secondary">
                                        Previous Value
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: '#ffebee', p: 1, borderRadius: 1 }}>
                                        {entry.details.oldValue}
                                      </Typography>
                                    </Grid>
                                  )}
                                  {entry.details.newValue && (
                                    <Grid item xs={12} md={4}>
                                      <Typography variant="caption" color="text.secondary">
                                        New Value
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: '#e8f5e9', p: 1, borderRadius: 1 }}>
                                        {entry.details.newValue}
                                      </Typography>
                                    </Grid>
                                  )}
                                  {entry.details.reason && (
                                    <Grid item xs={12}>
                                      <Typography variant="caption" color="text.secondary">
                                        Reason
                                      </Typography>
                                      <Typography variant="body2">
                                        {entry.details.reason}
                                      </Typography>
                                    </Grid>
                                  )}
                                  {entry.relatedEntity && (
                                    <Grid item xs={12}>
                                      <Typography variant="caption" color="text.secondary">
                                        Related Entity
                                      </Typography>
                                      <Typography variant="body2">
                                        {entry.relatedEntity}
                                      </Typography>
                                    </Grid>
                                  )}
                                </Grid>
                              )}

                              {!entry.details && (
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                  No additional details available
                                </Typography>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Keyboard Navigation Help */}
        <Paper sx={{ p: 2, mt: 2, bgcolor: '#f5f5f5' }}>
          <Typography variant="caption" color="text.secondary">
            <strong>Keyboard Navigation:</strong> Use ↑↓ arrow keys to navigate, Enter/Space to expand/collapse, Esc to clear focus
          </Typography>
        </Paper>
      </Box>
  );
};

export default EnhancedHistoryTab;
